/**
 * AdaptiveResolution — honest dynamic-resolution scaling under vsync.
 *
 * THE PROBLEM WITH NAIVE ADAPTIVE RES: requestAnimationFrame is vsync-locked, so
 * the frame interval tops out at the display refresh (16.6ms @60Hz). You can
 * MEASURE "we missed the deadline" (a long frame = the GPU couldn't finish in
 * time) but you can NEVER measure spare headroom above the cap — a device using
 * 6ms of its budget and one using 16ms both report a flat 60fps. The only true
 * headroom signal is a GPU timer query (EXT_disjoint_timer_query), which iOS
 * deliberately doesn't expose. So "should I raise?" is fundamentally unmeasurable
 * on the device that needs it most.
 *
 * Classic adaptive systems oscillate precisely because they RAISE to probe for
 * headroom they can't see, overshoot into dropped frames, back off, and repeat.
 *
 * THIS DESIGN removes that failure mode by making the two directions asymmetric:
 *   - DOWN is continuous and measured: sustained long frames (felt jank, sub-~45
 *     fps) during ACTIVE rendering → step the DPR cap down one rung. Reliable.
 *   - UP is never a free-running hunt. It only happens on a real reason to think
 *     conditions changed — app resume, a Battery charging event (where the API
 *     exists, i.e. not iOS), or a long perfectly-stable spell — and each up-step
 *     is VERIFIED over a short window and reverted + exponentially backed off if
 *     jank reappears. A settled device stays settled; it physically can't ping-
 *     pong (no continuous upward pressure, and failed probes get rarer over time).
 *
 * Real-world payoff: iOS Low Power Mode (the yellow battery) throttles the GPU →
 * frames sag → we quietly drop to 1.5 so the game stays SMOOTH (the player feels
 * "a touch softer", never a mysterious stutter). When they leave Low Power Mode
 * and the app resumes, the next probe recovers full retina. They never know.
 *
 * Sampling is gated on RenderScheduler.isActive() so the render-on-demand idle
 * heartbeat (8fps by design) is never mistaken for jank.
 */

import { SharedRenderer } from './SharedRenderer';

// A frame slower than this reads as felt jank (≈ below 45fps). Panel-agnostic:
// on a 120Hz screen we deliberately do NOT downscale just for missing 120 — only
// when the player would actually feel it.
const LONG_FRAME_MS = 22;
const WINDOW = 64;              // active frames kept in the rolling window
const MIN_SAMPLES = 48;         // need a near-full window before any down decision
const DOWN_FRACTION = 0.22;     // ≥22% long frames in the window → struggling
const STEP = 0.25;              // DPR rung size
const FLOOR_DPR = 1.5;          // never below this (looks soft past here)
const DECIDE_EVERY_MS = 250;    // throttle decisions (not every frame)
const WARMUP_MS = 2500;         // ignore jank right after start / a change / resume
const DOWN_COOLDOWN_MS = 4000;  // settle time after a down-step
const CONTINUITY_GAP_MS = 200;  // a bigger gap = came from idle/pause → break the chain

// Up-probe pacing (all oscillation-safety lives here).
const UP_STABLE_MS = 8000;       // no long frame for this long before a periodic probe
const UP_PROBE_BASE_MS = 60_000; // first periodic probe after settling
const UP_PROBE_MAX_MS = 15 * 60_000; // backoff ceiling
const UP_VERIFY_MS = 4000;       // watch this long after an up-step before trusting it

const STORE_KEY = 'sdl_adaptive_dpr_cap';

type Mode = 'idle' | 'verifying-up';

class AdaptiveResolution {
  private enabled = false;
  private ceiling = 2;            // highest DPR this device/tier can reach
  private cap = 2;                // current adaptive cap (a rung between FLOOR and ceiling)

  private intervals: number[] = [];
  private longCount = 0;
  private lastSampleAt = 0;
  private lastDecideAt = 0;

  private mode: Mode = 'idle';
  private graceUntil = 0;         // warmup / post-change grace (no decisions)
  private lastDownAt = 0;
  private lastLongAt = 0;         // last time we saw a long frame (stability clock)
  private verifyUntil = 0;
  private probedFrom = 2;         // cap before the in-flight up-probe
  private nextUpProbeAt = Infinity;
  private upBackoffMs = UP_PROBE_BASE_MS;

  /** Begin adapting. No-op if there's no headroom to give (ceiling already at floor). */
  start(now = performance.now()): void {
    if (this.enabled) return;
    this.ceiling = SharedRenderer.instance.maxResolvableDpr;
    // Nothing to adapt if the device can't even reach one rung above the floor.
    if (this.ceiling - FLOOR_DPR < STEP - 1e-6) return;

    this.enabled = true;
    this.cap = this.loadPersistedCap();
    SharedRenderer.instance.setAdaptiveDprCap(this.cap);

    this.graceUntil = now + WARMUP_MS;
    this.lastLongAt = now;
    // If we booted below full (persisted from a prior soft session), schedule a
    // recovery probe so a one-off bad day doesn't lock the device down forever.
    this.nextUpProbeAt = this.cap < this.ceiling ? now + UP_PROBE_BASE_MS : Infinity;
    this.upBackoffMs = UP_PROBE_BASE_MS;

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibility);
    }
    this.wireBattery();
    this.log(`enabled — ceiling ${this.ceiling}, floor ${FLOOR_DPR}, starting at ${this.cap}`);
  }

  stop(): void {
    if (!this.enabled) return;
    this.enabled = false;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  /**
   * Record one rendered frame. Call from the render driver ONLY when the frame
   * is a full-rate active frame (renderScheduler.isActive()), so the idle
   * heartbeat never registers as jank. Cheap; safe per frame.
   */
  sample(now: number): void {
    if (!this.enabled) return;

    const dt = now - this.lastSampleAt;
    this.lastSampleAt = now;

    // A large gap means this frame follows an idle/paused stretch, not a dropped
    // frame — break the chain rather than count the gap as jank.
    if (dt > CONTINUITY_GAP_MS) {
      this.intervals.length = 0;
      this.longCount = 0;
      return;
    }

    // Maintain the rolling window + long-frame count.
    const isLong = dt > LONG_FRAME_MS;
    this.intervals.push(dt);
    if (isLong) { this.longCount++; this.lastLongAt = now; }
    if (this.intervals.length > WINDOW) {
      if (this.intervals.shift()! > LONG_FRAME_MS) this.longCount--;
    }

    if (now - this.lastDecideAt < DECIDE_EVERY_MS) return;
    this.lastDecideAt = now;
    this.decide(now);
  }

  private decide(now: number): void {
    // Verifying an up-step: the moment jank shows, the probe failed.
    if (this.mode === 'verifying-up') {
      if (this.longCount > 1) { this.failUpProbe(now); return; }
      if (now >= this.verifyUntil) this.succeedUpProbe(now);
      return;
    }

    if (now < this.graceUntil) return;

    // DOWN — measured, the trustworthy direction.
    if (
      this.cap > FLOOR_DPR &&
      this.intervals.length >= MIN_SAMPLES &&
      now - this.lastDownAt > DOWN_COOLDOWN_MS &&
      this.longCount / this.intervals.length >= DOWN_FRACTION
    ) {
      this.stepDown(now);
      return;
    }

    // UP — only when there's room, the recent past is perfectly smooth, and a
    // probe is actually due (periodic backoff or an external resume/charge event).
    if (
      this.cap < this.ceiling &&
      now >= this.nextUpProbeAt &&
      now - this.lastLongAt >= UP_STABLE_MS
    ) {
      this.beginUpProbe(now);
    }
  }

  private stepDown(now: number): void {
    const from = this.cap;
    this.cap = Math.max(FLOOR_DPR, +(this.cap - STEP).toFixed(2));
    SharedRenderer.instance.setAdaptiveDprCap(this.cap);
    this.log(`DPR ${from} → ${this.cap} (sustained jank)`);
    this.persist();
    this.lastDownAt = now;
    this.graceUntil = now + WARMUP_MS;
    this.resetWindow();
    // Don't immediately fight a fresh downscale with a probe.
    this.nextUpProbeAt = now + this.upBackoffMs;
  }

  private beginUpProbe(now: number): void {
    this.probedFrom = this.cap;
    this.cap = Math.min(this.ceiling, +(this.cap + STEP).toFixed(2));
    SharedRenderer.instance.setAdaptiveDprCap(this.cap);
    this.mode = 'verifying-up';
    this.verifyUntil = now + UP_VERIFY_MS;
    this.resetWindow();
  }

  private succeedUpProbe(now: number): void {
    this.mode = 'idle';
    this.log(`DPR raised to ${this.cap} (held — probe succeeded)`);
    this.persist();
    this.upBackoffMs = UP_PROBE_BASE_MS;                 // reset backoff on success
    this.nextUpProbeAt = this.cap < this.ceiling ? now + UP_PROBE_BASE_MS : Infinity;
    this.graceUntil = now + WARMUP_MS;
    this.resetWindow();
  }

  private failUpProbe(now: number): void {
    this.cap = this.probedFrom;
    SharedRenderer.instance.setAdaptiveDprCap(this.cap);
    this.mode = 'idle';
    this.upBackoffMs = Math.min(UP_PROBE_MAX_MS, this.upBackoffMs * 2); // back off, exponentially
    this.log(`DPR probe to ${+(this.cap + STEP).toFixed(2)} failed → held at ${this.cap}, next try in ${Math.round(this.upBackoffMs / 1000)}s`);
    this.persist();
    this.nextUpProbeAt = now + this.upBackoffMs;
    this.lastDownAt = now;                               // treat like a downscale for cooldown
    this.graceUntil = now + WARMUP_MS;
    this.resetWindow();
  }

  private resetWindow(): void {
    this.intervals.length = 0;
    this.longCount = 0;
  }

  /** Allow an up-probe as soon as the device next looks stable (external signal). */
  private requestUpProbe(now = performance.now()): void {
    if (!this.enabled || this.cap >= this.ceiling) return;
    this.nextUpProbeAt = now;          // due now; still gated on UP_STABLE_MS calm
    this.graceUntil = now + WARMUP_MS; // give the resume a moment before judging
    this.resetWindow();
  }

  private onVisibility = (): void => {
    if (typeof document !== 'undefined' && !document.hidden) this.requestUpProbe();
  };

  private wireBattery(): void {
    // Battery API: Android/Chromium only — iOS Safari never exposed it, so this is
    // a bonus precise trigger where available, not a dependency.
    const nav = navigator as Navigator & { getBattery?: () => Promise<any> };
    if (typeof nav.getBattery !== 'function') return;
    nav.getBattery().then((bat) => {
      const onChange = () => this.requestUpProbe();
      bat.addEventListener('chargingchange', onChange);  // plugged in → likely un-throttled
      bat.addEventListener('levelchange', onChange);
    }).catch(() => { /* unsupported / blocked — fine */ });
  }

  private loadPersistedCap(): number {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw == null) return this.ceiling;
      const v = parseFloat(raw);
      if (!Number.isFinite(v)) return this.ceiling;
      return Math.min(this.ceiling, Math.max(FLOOR_DPR, v));
    } catch {
      return this.ceiling;
    }
  }

  private persist(): void {
    try { localStorage.setItem(STORE_KEY, String(this.cap)); } catch { /* private mode */ }
  }

  /** Low-noise diagnostic — cap changes are rare, so this won't spam. Also mirrors
   *  the live value onto window.__adaptiveDpr for quick inspection (incl. Safari
   *  Web Inspector on the installed app). */
  private log(msg: string): void {
    console.log(`%c[adaptive] ${msg}`, 'color:#00F5FF;font-weight:bold');
    (window as unknown as { __adaptiveDpr?: number }).__adaptiveDpr = this.cap;
  }
}

/** App-wide singleton. main() starts it; the render driver feeds it sample(now). */
export const adaptiveResolution = new AdaptiveResolution();
